import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SearchInput from "./SearchInput";
import UserSearchResults from "./UserSearchResults";
import { useUserSearch } from "../../hooks/user/useUserSearch";

/**
 * Desktop search bar component with dropdown results
 *
 * Features:
 * - Compact search input in header
 * - Dropdown with live search results
 * - Click outside to close
 * - Keyboard navigation (Escape to close)
 * - "View all results" link to full search page
 */
const SearchBar = ({ className = "" }) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch search results
  const { data, isLoading, isError, error } = useUserSearch(query, {
    limit: 5, // Show max 5 results in dropdown
  });

  const users = data?.users || [];
  const totalResults = data?.pagination?.total || 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Clear query when closing dropdown
        setQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        // Clear query when closing with Escape
        setQuery("");
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isOpen]);

  // Handle search input change
  const handleSearchChange = (newQuery) => {
    setQuery(newQuery);
    if (newQuery.trim().length >= 2) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  // Handle clear
  const handleClear = () => {
    setQuery("");
    setIsOpen(false);
  };

  // Handle user click - navigate to profile and close dropdown
  const handleUserClick = (user) => {
    const userId = user._id || user.id;
    if (userId) {
      navigate(`/users/${userId}`);
      setIsOpen(false);
      setQuery(""); // Clear query after navigation
    }
  };

  // Handle "View all results" click
  const handleViewAll = () => {
    navigate(`/search?q=${encodeURIComponent(query)}`);
    setIsOpen(false);
    setQuery(""); // Clear query after navigation
  };

  // Show dropdown when there's a query and results (or loading)
  const shouldShowDropdown = isOpen && query.trim().length >= 2;

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Search Input */}
      <SearchInput
        value={query}
        onChange={handleSearchChange}
        onClear={handleClear}
        placeholder="Search users..."
        className="w-64"
        inputClassName="text-sm"
      />

      {/* Dropdown Results */}
      {shouldShowDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-light-grey z-50 max-h-96 overflow-y-auto">
          <div className="p-3">
            <UserSearchResults
              users={users}
              isLoading={isLoading}
              isError={isError}
              error={error}
              query={query}
              compact={true}
              onUserClick={handleUserClick}
              maxResults={5}
            />

            {/* View All Results Link */}
            {!isLoading && !isError && users.length > 0 && (
              <div className="mt-3 pt-3 border-t border-light-grey">
                <button
                  onClick={handleViewAll}
                  className="w-full text-center text-sm font-medium text-gold hover:text-deep-red transition-colors py-2"
                >
                  View all {totalResults} result{totalResults !== 1 ? "s" : ""}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
